import type { ExtendedDocumentReference } from '@seen/core/models/document-references/documentReferences'
import type { UseClientSideSearchOptions } from '#src/hooks/useClientSideSearch.ts'
import { extractTextFromHTML } from '#src/utils/html.ts'

interface ExtractDocSearchFieldsResult {
  [key: string]: string
}

/* `description` is RichTextEditor HTML — we MUST strip tags before
 * indexing or MiniSearch tokenizes `"<p>diabetes"` as a literal
 * `"<p>diabetes"` token (stem `"<p>diabet"`), which never matches the
 * stemmed query token `"diabet"`. `aiSummary` is plaintext from the
 * LLM bot today, but we strip defensively so a future RichText migration
 * doesn't regress search. `extractTextFromHTML` is a no-op on plaintext. */
export function extractDocSearchFields(
  doc: ExtendedDocumentReference
): ExtractDocSearchFieldsResult {
  return {
    title: doc.attachments
      .map((a) => a.title)
      .filter((t): t is string => !!t)
      .join(' '),
    description: extractTextFromHTML(doc.description ?? ''),
    aiSummary: extractTextFromHTML(doc.aiSummary ?? ''),
    transcription: doc.transcription ?? '',
    author: doc.author?.display ?? '',
  }
}

/* Field boosts reflect prominence (title > aiSummary > author >
 * description > transcription). `category` is a tab/badge facet, not an
 * indexed field. Shared so the participant Documents tab and the global
 * Uploaded Documents page rank the same query identically. */
export const DOCUMENT_SEARCH_OPTIONS: Pick<
  UseClientSideSearchOptions<ExtendedDocumentReference>,
  'fields' | 'boost'
> = {
  fields: ['title', 'description', 'aiSummary', 'transcription', 'author'],
  boost: {
    title: 5,
    aiSummary: 3,
    author: 2,
    description: 1.5,
    transcription: 1,
  },
}
